import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useDialog } from '../context/DialogContext';
import { useAuth } from '../context/AuthContext';
import { customAuth } from '../services/customAuth';
import { consumeAuthReturnTo } from '../utils/authReturn';
import { useLocale } from '../context/LocaleContext';
import styles from './VerifyEmailPage.module.css';

const VerifyEmailPage: React.FC = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { showAlert, showConfirm } = useDialog();
  const { refreshUserState } = useAuth();
  const { t } = useLocale();
  
  const [isVerifying, setIsVerifying] = useState(false);
  const [verificationStatus, setVerificationStatus] = useState<'pending' | 'success' | 'error'>('pending');
  const [errorMessage, setErrorMessage] = useState('');
  const [verificationCode, setVerificationCode] = useState('');
  
  // Get parameters from URL
  const email = searchParams.get('email');
  const username = searchParams.get('username');
  const timestamp = searchParams.get('timestamp');
  const signature = searchParams.get('signature');
  const action = searchParams.get('action');
  const code = searchParams.get('code'); // For manual verification

  useEffect(() => {
    // If we have auto-confirmation parameters, attempt auto-verification
    if (action === 'auto-confirm' && email && timestamp && signature) {
      handleAutoConfirmation();
    } else if (code && email) {
      // Manual verification with code
      handleManualVerification();
    } else {
      // No valid parameters, show manual verification form
      setVerificationStatus('pending');
    }
  }, [action, email, timestamp, signature, code]);

  const handleAutoConfirmation = async () => {
    setIsVerifying(true);
    setErrorMessage('');
    
    try {
      // For now, we'll simulate auto-confirmation since the Lambda generates the URL
      // In a production system, you'd verify the signature server-side
      
      // Show success message
      setVerificationStatus('success');
      showAlert({
        message: t('auth.verify.verified_sign_in'),
        variant: 'success'
      });
      
      // Redirect after a delay
      setTimeout(() => {
        navigate(consumeAuthReturnTo(searchParams));
      }, 3000);
      
    } catch (error: any) {
      setVerificationStatus('error');
      setErrorMessage(error.message || t('auth.server.verify_failed'));
    } finally {
      setIsVerifying(false);
    }
  };

  const handleManualVerification = async () => {
    if (!verificationCode || !email) {
      setErrorMessage(t('auth.verify.code_required'));
      return;
    }

    setIsVerifying(true);
    setErrorMessage('');

    try {
      const result = await customAuth.confirmSignUp(email, verificationCode);
      
      if (result.success) {
        setVerificationStatus('success');
        
        // Refresh auth state to log the user in automatically
        await refreshUserState();
        
        showAlert({
          message: t('auth.verify.verified_logged_in'),
          variant: 'success'
        });
        
        // Redirect to the page they started from
        setTimeout(() => {
          navigate(consumeAuthReturnTo(searchParams));
        }, 2000);
      } else {
        setVerificationStatus('error');
        setErrorMessage(result.message || t('auth.server.verify_failed'));
      }
      
    } catch (error: any) {
      setVerificationStatus('error');
      setErrorMessage(error.message || t('auth.server.verify_failed'));
    } finally {
      setIsVerifying(false);
    }
  };

  const handleResendCode = async () => {
    if (!email) {
      setErrorMessage(t('auth.verify.email_required'));
      return;
    }

    setIsVerifying(true);
    setErrorMessage('');

    try {
      const result = await customAuth.resendConfirmationCode(email);
      if (!result.success) {
        setErrorMessage(result.message);
        return;
      }
      setVerificationStatus('pending');
      setVerificationCode('');
      showAlert({
        message: t('auth.verify.resent'),
        variant: 'success'
      });
    } catch (error: any) {
      setErrorMessage(error.message || t('auth.server.resend_failed'));
    } finally {
      setIsVerifying(false);
    }
  };

  const handleGoToLogin = () => {
    navigate(consumeAuthReturnTo(searchParams));
  };

  if (verificationStatus === 'success') {
    return (
      <div className={styles.container}>
        <div className={styles.card}>
          <div className={styles.successIcon}>✅</div>
          <h1>{t('auth.verify.success_title')}</h1>
          <p>{t('auth.verify.success_body')}</p>
          <button onClick={() => navigate(consumeAuthReturnTo(searchParams))} className={styles.primaryButton}>
            {t('auth.verify.continue')}
          </button>
        </div>
      </div>
    );
  }

  if (verificationStatus === 'error') {
    return (
      <div className={styles.container}>
        <div className={styles.card}>
          <div className={styles.errorIcon}>❌</div>
          <h1>{t('auth.verify.failed_title')}</h1>
          <p className={styles.errorMessage}>{errorMessage}</p>
          <div className={styles.buttonGroup}>
            <button onClick={handleResendCode} disabled={isVerifying} className={styles.secondaryButton}>
              {t('auth.verify.resend_short')}
            </button>
            <button onClick={handleGoToLogin} className={styles.primaryButton}>
              {t('auth.verify.go_to_sign_in')}
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className={styles.container}>
      <div className={styles.card}>
        <h1>{t('auth.verify.title')}</h1>
        
        {email && (
          <div className={styles.emailInfo}>
            <p>{t('auth.verify.sent_to')}</p>
            <p className={styles.email}>{email}</p>
          </div>
        )}

        <div className={styles.spamWarning}>
          <p><strong>📧 {t('auth.verify.check_email')}</strong></p>
          <p>
            {t('auth.verify.from_label')} <strong>no-reply@recoverycommons.com</strong>
          </p>
          <p>{t('auth.verify.spam_hint')}</p>
        </div>

        <div className={styles.verificationForm}>
          <div className={styles.formGroup}>
            <label htmlFor="verificationCode">{t('auth.verify.code_label')}</label>
            <input
              id="verificationCode"
              type="text"
              placeholder={t('auth.verify.code_placeholder')}
              maxLength={6}
              value={verificationCode}
              onChange={(e) => setVerificationCode(e.target.value)}
            />
          </div>

          <button 
            onClick={handleManualVerification}
            disabled={isVerifying}
            className={styles.primaryButton}
          >
            {isVerifying ? t('auth.verify.verifying') : t('auth.verify.submit')}
          </button>
        </div>

        <div className={styles.helpSection}>
          <p>{t('auth.verify.no_code')}</p>
          <button 
            onClick={handleResendCode}
            disabled={isVerifying}
            className={styles.linkButton}
          >
            {t('auth.verify.resend')}
          </button>
        </div>

        <div className={styles.backToLogin}>
          <button onClick={handleGoToLogin} className={styles.linkButton}>
            ← {t('auth.form.back_to_sign_in')}
          </button>
        </div>
      </div>
    </div>
  );
};

export default VerifyEmailPage;
